import { useMemo, useState, type CSSProperties } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { Doughnut, Line } from "react-chartjs-2";
import { useAuth, podeLancar } from "../lib/AuthContext";
import { useLancamentos, useTiposAtividade, type Lancamento, type TipoAtividade } from "../lib/queries";
import { supabase } from "../lib/supabase";
import { AnimatedList } from "../components/ui/animated-list";
import { ShimmerButton } from "../components/ui/shimmer-button";

ChartJS.register(ArcElement, CategoryScale, LinearScale, LineElement, PointElement, Filler, Tooltip, Legend);

const CHART_FONT = { family: "'Instrument Sans', sans-serif", size: 12, weight: 600 as const };

const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MONTHS_LONG = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];
const MONTHS_SHORT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const ARP_TYPES = ["alto_papos", "cef_cine", "apa"];
const ARP_GOAL_KEY = "cef_arp_goal";

type Enriquecido = Lancamento & {
  realized: boolean;
  color: string;
  soft: string;
  typeLabel: string;
  dd: string;
  day: string;
  m: number;
  d: number;
  dateShort: string;
};

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

export function Dashboard() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [activeTypes, setActiveTypes] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [apaEnabled, setApaEnabled] = useState(true);
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [viewMonths, setViewMonths] = useState<number[]>([new Date().getMonth() + 1]);
  const [showSettings, setShowSettings] = useState(false);
  const [arpGoal, setArpGoal] = useState(() => parseInt(localStorage.getItem(ARP_GOAL_KEY) || "6", 10));

  const { data: tipos } = useTiposAtividade();
  const { data: lancamentos } = useLancamentos(viewYear);

  const tipoMap = useMemo(() => {
    const map = new Map<string, TipoAtividade>();
    tipos?.forEach((t) => map.set(t.slug, t));
    return map;
  }, [tipos]);

  const metaOf = (slug: string) => tipoMap.get(slug) ?? { slug, label: slug, cor: "#B6AC96", cor_suave: "#EFE7D3", emoji: "", ordem: 0, ativo: true };

  const fullAll: Enriquecido[] = useMemo(() => {
    const source = (lancamentos ?? []).filter((l) => apaEnabled || l.tipo !== "apa");
    return source.map((l) => {
      const meta = metaOf(l.tipo);
      const [, m, d] = l.data.split("-").map(Number);
      const dateObj = new Date(l.data + "T00:00:00");
      return {
        ...l,
        realized: !l.cancelado && l.valor != null,
        color: meta.cor,
        soft: meta.cor_suave,
        typeLabel: meta.label,
        dd: pad2(d),
        day: WEEKDAYS[dateObj.getDay()],
        m,
        d,
        dateShort: `${pad2(d)}/${pad2(m)}`,
      };
    });
  }, [lancamentos, apaEnabled, tipoMap]);

  const monthly = useMemo(
    () => fullAll.filter((e) => e.m != null && viewMonths.includes(e.m)),
    [fullAll, viewMonths],
  );
  const hasMonthly = monthly.length > 0;
  const max = Math.max(15, ...monthly.filter((e) => e.valor != null).map((e) => e.valor as number));
  const chartH = 258;

  const sortedMonths = [...viewMonths].sort((a, b) => a - b);
  const monthLabelLong =
    sortedMonths.length === 1
      ? `${MONTHS_LONG[sortedMonths[0] - 1]}/${viewYear}`
      : `${sortedMonths.map((m) => MONTHS_SHORT[m - 1]).join(" + ")}/${viewYear}`;
  const monthLabelShort =
    sortedMonths.length === 1 ? `${MONTHS_SHORT[sortedMonths[0] - 1]}/${viewYear}` : `${sortedMonths.length} meses/${viewYear}`;

  function toggleMonth(m: number) {
    setViewMonths((cur) => {
      const has = cur.includes(m);
      if (has && cur.length === 1) return cur;
      return has ? cur.filter((x) => x !== m) : [...cur, m].sort((a, b) => a - b);
    });
  }
  function selectAllMonths() {
    setViewMonths((cur) => (cur.length === 12 ? [new Date().getMonth() + 1] : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]));
  }
  function toggleType(id: string) {
    setActiveTypes((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  }
  function changeArpGoal(n: number) {
    const val = Math.max(1, n || 1);
    localStorage.setItem(ARP_GOAL_KEY, String(val));
    setArpGoal(val);
  }

  // ---- ARP contract card ----
  const arpDone = fullAll.filter((e) => ARP_TYPES.includes(e.tipo) && e.realized);
  const arpCount = arpDone.length;
  const arpPct = Math.round((arpCount / arpGoal) * 100);
  const overflow = Math.max(0, arpCount - arpGoal);
  const arpCardBg = arpCount >= arpGoal
    ? "linear-gradient(120deg,#5C4A18 0%,#8C6B1F 50%,#2E4633 100%)"
    : "linear-gradient(120deg,#2E4633 0%,#3B5844 100%)";
  const arpRemainingLabel = overflow > 0
    ? `🏆 Meta superada em ${overflow} atividade${overflow > 1 ? "s" : ""}!`
    : arpCount === arpGoal ? "🎉 Meta atingida!" : `${arpGoal - arpCount} atividade(s) restante(s) no ano`;

  // ---- KPIs ----
  const muroVals = monthly.filter((e) => e.tipo === "muro" && e.realized).map((e) => e.valor as number);
  const muroSum = muroVals.reduce((a, b) => a + b, 0);
  const socialSum = monthly.filter((e) => e.tipo === "social" && e.realized).reduce((a, b) => a + (b.valor ?? 0), 0);
  const totalSum = monthly.filter((e) => e.realized).reduce((a, b) => a + (b.valor ?? 0), 0);
  const avg = muroVals.length ? (muroSum / muroVals.length).toFixed(1).replace(".", ",") : "—";
  const peak = [...monthly].filter((e) => e.tipo === "muro" && e.realized).sort((a, b) => (b.valor ?? 0) - (a.valor ?? 0))[0];
  const kpis = [
    { label: "Presenças no mês", value: totalSum, sub: `${monthly.length} encontros programados`, color: "#23281F", icon: "📊" },
    { label: "Muro de Escalada", value: muroSum, sub: `${muroVals.length} treinos realizados`, color: "#DB6B39", icon: "🧗" },
    { label: "Reunião Social", value: socialSum, sub: `${monthly.filter((e) => e.tipo === "social" && e.realized).length} encontros realizados`, color: "#4C6B4E", icon: "🤝" },
    { label: "Média por treino", value: avg, sub: `pico de ${peak?.valor ?? 0} em ${peak?.dateShort ?? "—"}`, color: "#3E6B8A", icon: "📈" },
  ];

  const filterSummary = activeTypes.length === 0 ? null : activeTypes.map((t) => {
    const meta = metaOf(t);
    const items = monthly.filter((e) => e.tipo === t);
    const sum = items.filter((e) => e.realized).reduce((a, b) => a + (b.valor ?? 0), 0);
    return { label: meta.label, sum, count: items.filter((e) => e.realized).length, color: meta.cor };
  });

  const typeMatches = (t: string) => activeTypes.length === 0 || activeTypes.includes(t);
  const visible = monthly.filter((e) => typeMatches(e.tipo));
  const gridlines = [5, 10, 15].map((g) => ({ label: g, bottom: (g / max) * chartH }));

  const sel = (selectedDate ? monthly.find((e) => e.data === selectedDate) : null) ?? monthly[0] ?? null;
  const selSoft = sel ? sel.soft : "#EFE7D3";

  const agenda = monthly;

  // ---- Doughnut: distribuição por atividade no período selecionado ----
  const distribuicaoTipos = (tipos ?? [])
    .map((t) => ({
      tipo: t,
      sum: monthly.filter((e) => e.tipo === t.slug && e.realized).reduce((a, b) => a + (b.valor ?? 0), 0),
    }))
    .filter((d) => d.sum > 0);
  const doughnutData = {
    labels: distribuicaoTipos.map((d) => d.tipo.label),
    datasets: [
      {
        data: distribuicaoTipos.map((d) => d.sum),
        backgroundColor: distribuicaoTipos.map((d) => d.tipo.cor),
        borderColor: "#FBF8F1",
        borderWidth: 2,
      },
    ],
  };

  // ---- Linha: presenças por mês ao longo do ano ----
  const presencasPorMes = MONTHS_SHORT.map((_, idx) =>
    fullAll.filter((e) => e.m === idx + 1 && e.realized).reduce((a, b) => a + (b.valor ?? 0), 0),
  );
  const lineData = {
    labels: MONTHS_SHORT,
    datasets: [
      {
        label: "Presenças",
        data: presencasPorMes,
        borderColor: "#DB6B39",
        backgroundColor: "#DB6B3933",
        pointBackgroundColor: "#DB6B39",
        pointBorderColor: "#FBF8F1",
        pointBorderWidth: 1.5,
        pointRadius: 4,
        tension: 0.35,
        fill: true,
      },
    ],
  };

  return (
    <div style={{ minHeight: "100vh", background: "radial-gradient(120% 80% at 85% -10%, #F6EFDF 0%, #EFE8DA 42%, #EAE1D0 100%)", padding: "34px clamp(20px,4vw,56px) 60px" }}>
      <div style={{ maxWidth: 1300, margin: "0 auto", display: "flex", flexDirection: "column", gap: 22 }}>
        {/* HEADER */}
        <header className="no-print" style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: 20, borderBottom: "1.5px solid #DDD2BC", paddingBottom: 22 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
            <img src="/logo-cef-colored.svg" alt="Centro Excursionista Friburguense" style={{ height: 66, width: "auto", flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: 2.5, textTransform: "uppercase", color: "#8E8672" }}>Sede CEF · Painel de Frequência</div>
              <h1 style={{ fontSize: "clamp(28px,3.6vw,42px)", letterSpacing: -1.6, lineHeight: 1, marginTop: 6 }}>Programação de {monthLabelLong}</h1>
            </div>
          </div>
          <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
            {tipos?.map((t) => {
              const active = activeTypes.includes(t.slug);
              return (
                <button
                  key={t.slug}
                  onClick={() => toggleType(t.slug)}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, border: `1.5px solid ${active ? t.cor : "transparent"}`,
                    background: active ? `${t.cor}1A` : "transparent", cursor: "pointer", padding: "5px 10px", borderRadius: 9,
                    fontSize: 13.5, fontWeight: 600, color: "#4A4A3E",
                  }}
                >
                  <span style={{ width: 11, height: 11, borderRadius: 4, background: t.cor, flexShrink: 0 }} />
                  <span>{t.label}</span>
                </button>
              );
            })}
            <div style={{ width: 1.5, height: 22, background: "#DDD2BC" }} />
            {podeLancar(profile) && (
              <ShimmerButton
                onClick={() => navigate("/lancar")}
                background="#DB6B39"
                shimmerColor="#FBF8F1"
                borderRadius="10px"
                className="px-4 py-2.5 text-[13.5px] font-bold"
              >
                + Lançar dados
              </ShimmerButton>
            )}
            <Link to="/imprimir" className="btn">Imprimir</Link>
            {profile?.papel === "admin" && (
              <button onClick={() => setShowSettings((s) => !s)} className="btn" style={{ width: 38, height: 38, padding: 0, fontSize: 17 }}>⚙️</button>
            )}
            <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{profile?.nome}</span>
            <button className="btn" onClick={() => supabase.auth.signOut()}>Sair</button>
          </div>
        </header>

        {showSettings && (
          <div className="card no-print" style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: -6 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: "#4A4A3E" }}>Meta anual da Contrapartida ARP</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input type="number" min={1} className="input" style={{ width: 90 }} value={arpGoal} onChange={(e) => changeArpGoal(parseInt(e.target.value, 10))} />
              <span style={{ fontSize: 12.5, color: "#9A9280" }}>atividades/ano (Alto Papos, CEF Cine, APA)</span>
            </div>
          </div>
        )}

        {/* YEAR / MONTH FILTER */}
        <section className="card no-print" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
            <button className="btn" style={{ width: 32, height: 32, padding: 0 }} onClick={() => setViewYear((y) => y - 1)}>‹</button>
            <strong style={{ fontFamily: "var(--font-heading)", fontSize: 19 }}>{viewYear}</strong>
            <button className="btn" style={{ width: 32, height: 32, padding: 0 }} onClick={() => setViewYear((y) => y + 1)}>›</button>
          </div>
          <div style={{ display: "flex", gap: 5, overflowX: "auto", flex: 1, minWidth: 0 }}>
            {MONTHS_SHORT.map((label, idx) => {
              const mnum = idx + 1;
              const active = viewMonths.includes(mnum);
              return (
                <button
                  key={mnum}
                  onClick={() => toggleMonth(mnum)}
                  style={{
                    border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, padding: "7px 13px", borderRadius: 9, flexShrink: 0,
                    background: active ? "#DB6B39" : "#F0EADB", color: active ? "#FBF8F1" : "#847C68",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <button className="btn" onClick={selectAllMonths}>Ano todo</button>
          <button
            className="btn"
            title="Ativa/desativa a contagem do APA nos totais"
            style={{ background: apaEnabled ? "#FFFCF4" : "#EFE7D3", color: apaEnabled ? "#6B6350" : "#B08D3F" }}
            onClick={() => setApaEnabled((v) => !v)}
          >
            {apaEnabled ? "APA: contando" : "APA: pausado"}
          </button>
        </section>

        {/* ARP CARD */}
        <section style={{ background: arpCardBg, borderRadius: 22, padding: "24px 28px", color: "#F5EBD8", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 24, flexWrap: "wrap", boxShadow: "0 10px 26px -14px #2E463388" }}>
          <div style={{ minWidth: 260, flex: "1 1 260px" }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", opacity: 0.72 }}>Contrapartida · Contrato ARP {viewYear}</div>
            <div style={{ fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 26, lineHeight: 1.28, letterSpacing: -0.8, marginTop: 8 }}>{arpCount} de {arpGoal} atividades cumpridas</div>
            <div style={{ fontSize: 13, opacity: 0.9, marginTop: 8, lineHeight: 1.4, fontWeight: 600 }}>{arpRemainingLabel}</div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", maxWidth: 280 }}>
            {Array.from({ length: Math.min(arpCount, arpGoal) }, (_, i) => {
              const done = arpDone[i];
              return <div key={i} title={`${done.typeLabel} · ${done.dateShort}`} style={{ width: 30, height: 30, borderRadius: 9, background: done.color, boxShadow: `0 4px 10px -3px ${done.color}aa` }} />;
            })}
            {Array.from({ length: Math.max(0, arpGoal - arpCount) }, (_, i) => (
              <div key={`p${i}`} title="pendente" style={{ width: 30, height: 30, borderRadius: 9, border: "1.5px dashed #F5EBD866" }} />
            ))}
            {overflow > 0 && (
              <div title={`${overflow} atividade(s) além da meta`} style={{ width: 30, height: 30, borderRadius: 9, background: "linear-gradient(150deg,#F4C752,#D9A02A)", boxShadow: "0 4px 14px -3px #F4C75299", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 12, color: "#3B2E0A" }}>
                +{overflow}
              </div>
            )}
          </div>
          <div style={{ textAlign: "right", minWidth: 150 }}>
            <div style={{ fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 34, letterSpacing: -1 }}>{arpPct}%{overflow > 0 ? " 🚀" : ""}</div>
            <div style={{ fontSize: 12, opacity: 0.78 }}>Alto Papos, CEF Cine e APA</div>
          </div>
        </section>

        {/* KPI ROW */}
        <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16 }}>
          {kpis.map((k) => (
            <div key={k.label} className="card" style={{ display: "flex", flexDirection: "column", gap: 8, position: "relative" }}>
              <div style={{ position: "absolute", top: 16, right: 18, fontSize: 22, opacity: 0.9 }}>{k.icon}</div>
              <div style={{ fontSize: 12.5, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", color: "#9A9280" }}>{k.label}</div>
              <div style={{ fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 42, lineHeight: 0.95, letterSpacing: -2, fontVariantNumeric: "tabular-nums", color: k.color }}>{k.value}</div>
              <div style={{ fontSize: 13, color: "#7C7462", fontWeight: 500 }}>{k.sub}</div>
            </div>
          ))}
        </section>

        {/* MAIN GRID */}
        <div style={{ display: "grid", gridTemplateColumns: "1.65fr 1fr", gap: 22, alignItems: "start" }}>
          {/* CHART CARD */}
          <section className="card" style={{ borderRadius: 22, padding: "24px 26px 20px" }}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 14, marginBottom: 8 }}>
              <div>
                <h2 style={{ fontSize: 22, letterSpacing: -0.8 }}>Frequência em {monthLabelLong}</h2>
                <p style={{ fontSize: 13.5, color: "#8E8672", fontWeight: 500, marginTop: 2 }}>Pessoas por dia · toque numa barra para ver detalhes</p>
              </div>
              <div style={{ display: "flex", background: "#F0EADB", border: "1.5px solid #E3DAC6", borderRadius: 12, padding: 4, gap: 3 }}>
                <button onClick={() => setActiveTypes([])} style={{ border: "none", cursor: "pointer", fontSize: 13.5, fontWeight: 600, padding: "7px 16px", borderRadius: 9, background: activeTypes.length === 0 ? "#DB6B39" : "transparent", color: activeTypes.length === 0 ? "#FBF8F1" : "#847C68" }}>Tudo</button>
                {tipos?.map((t) => {
                  const active = activeTypes.includes(t.slug);
                  return (
                    <button key={t.slug} onClick={() => toggleType(t.slug)} style={{ border: "none", cursor: "pointer", fontSize: 13.5, fontWeight: 600, padding: "7px 16px", borderRadius: 9, background: active ? "#DB6B39" : "transparent", color: active ? "#FBF8F1" : "#847C68" }}>
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {filterSummary && (
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
                {filterSummary.map((fs) => (
                  <div key={fs.label} style={{ display: "flex", alignItems: "center", gap: 7, background: "#F0EADB", borderRadius: 10, padding: "6px 12px" }}>
                    <span style={{ width: 9, height: 9, borderRadius: "50%", background: fs.color }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#4A4A3E" }}>{fs.label}:</span>
                    <span style={{ fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 14, color: fs.color }}>{fs.sum} pessoas</span>
                    <span style={{ fontSize: 12, color: "#9A9280" }}>({fs.count} encontros)</span>
                  </div>
                ))}
              </div>
            )}

            {hasMonthly ? (
              <>
                <div style={{ position: "relative", height: chartH + 30, marginTop: 10 }}>
                  {gridlines.map((g) => (
                    <div key={g.label} style={{ position: "absolute", left: 20, right: 0, bottom: g.bottom, height: 1, background: "repeating-linear-gradient(to right,#E3DAC6 0 6px,transparent 6px 12px)" }}>
                      <span style={{ position: "absolute", left: -2, top: -9, fontSize: 11, fontWeight: 600, color: "#B6AC96", fontVariantNumeric: "tabular-nums" }}>{g.label}</span>
                    </div>
                  ))}
                  <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "flex-end", gap: "2%", paddingLeft: 20 }}>
                    {visible.map((e) => {
                      const isSel = e.data === sel?.data;
                      const h = e.realized ? Math.max(14, ((e.valor as number) / max) * chartH) : 16;
                      const barStyle: CSSProperties = e.realized
                        ? { width: "100%", maxWidth: 46, height: h, borderRadius: "9px 9px 4px 4px", background: e.color, opacity: isSel ? 1 : 0.9, outline: isSel ? `3px solid ${e.color}55` : undefined, outlineOffset: 2, boxShadow: isSel ? `0 8px 18px -6px ${e.color}99` : undefined }
                        : { width: "100%", maxWidth: 46, height: h, borderRadius: 8, background: "repeating-linear-gradient(45deg,#EAE1CF 0 5px,#F3ECDD 5px 10px)", border: "1.5px dashed #D2C7AE", outline: isSel ? "3px solid #D2C7AE66" : undefined, outlineOffset: 2 };
                      return (
                        <div key={e.id} onClick={() => setSelectedDate(e.data)} style={{ flex: 1, minWidth: 0, height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center", gap: 7, cursor: "pointer" }}>
                          <div style={{ fontFamily: "var(--font-heading)", fontWeight: 700, fontSize: 14, fontVariantNumeric: "tabular-nums", color: e.realized ? (isSel ? e.color : "#6B6350") : "#C4BBA4" }}>
                            {e.realized ? e.valor : "—"}
                          </div>
                          <div style={barStyle} />
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div style={{ display: "flex", gap: "2%", paddingLeft: 20, marginTop: 10, borderTop: "1.5px solid #EDE6D6", paddingTop: 10 }}>
                  {visible.map((e) => {
                    const isSel = e.data === sel?.data;
                    return (
                      <div key={e.id} onClick={() => setSelectedDate(e.data)} style={{ flex: 1, minWidth: 0, textAlign: "center", cursor: "pointer" }}>
                        <div style={{ fontFamily: "var(--font-heading)", fontWeight: isSel ? 700 : 600, fontSize: 12.5, fontVariantNumeric: "tabular-nums", color: isSel ? e.color : "#8E8672" }}>{e.dateShort}</div>
                        <div style={{ fontSize: 11, color: "#A79E88", fontWeight: 500, marginTop: 1 }}>{e.day}</div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div style={{ height: 288, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, color: "#A79E88" }}>
                <div style={{ fontSize: 36 }}>🗓️</div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>Nenhuma atividade registrada em {monthLabelLong}</div>
              </div>
            )}
          </section>

          {/* RIGHT COLUMN */}
          <section style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div style={{ background: selSoft, border: `1.5px solid ${sel ? sel.color : "#D2C7AE"}33`, borderRadius: 22, padding: "22px 24px", boxShadow: `0 2px 0 ${sel ? sel.color : "#D2C7AE"}22` }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", color: "#FBF8F1", background: sel ? sel.color : "#B6AC96", padding: "5px 11px", borderRadius: 8 }}>
                  {sel ? sel.typeLabel : "—"}
                </span>
                <span style={{ fontFamily: "var(--font-heading)", fontWeight: 600, fontSize: 15, color: "#6B6350", fontVariantNumeric: "tabular-nums" }}>
                  {sel ? `${sel.dd} ${MONTHS_SHORT[sel.m - 1]} · ${sel.day}` : "—"}
                </span>
              </div>
              <h3 style={{ fontSize: 23, letterSpacing: -0.7, lineHeight: 1.12, marginTop: 14, color: "#23281F" }}>
                {sel ? sel.titulo || sel.typeLabel : "Sem seleção"}
              </h3>
              {sel?.subtitulo && <p style={{ fontSize: 14, color: "#6E6653", fontWeight: 500, marginTop: 6, fontStyle: "italic" }}>{sel.subtitulo}</p>}
              <div style={{ display: "flex", alignItems: "flex-end", gap: 12, marginTop: 20 }}>
                <span style={{ fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 64, lineHeight: 0.8, letterSpacing: -3, color: sel?.realized ? sel.color : "#B6AC96", fontVariantNumeric: "tabular-nums" }}>
                  {sel?.realized ? sel.valor : "—"}
                </span>
                <span style={{ fontSize: 15, fontWeight: 600, color: "#7C7462", paddingBottom: 8 }}>
                  {sel?.realized ? (sel.valor === 1 ? "pessoa presente" : "pessoas presentes") : sel?.cancelado ? "cancelado" : "Sem registro"}
                </span>
              </div>
            </div>

            <div className="card" style={{ borderRadius: 22, padding: "20px 20px 12px" }}>
              <h2 style={{ fontSize: 18, letterSpacing: -0.5, marginBottom: 12 }}>Agenda de {monthLabelShort} ({agenda.length})</h2>
              <div style={{ maxHeight: 360, overflowY: "auto" }}>
                <AnimatedList
                  key={`${viewYear}-${viewMonths.join(",")}-${activeTypes.join(",")}-${apaEnabled}`}
                  delay={120}
                  className="!items-stretch !gap-0"
                >
                  {agenda.map((e) => {
                    const isSel = e.data === sel?.data;
                    return (
                      <div key={e.id} onClick={() => setSelectedDate(e.data)} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 12px", borderRadius: 14, cursor: "pointer", margin: "0 -6px", background: isSel ? "#F1EADB" : "transparent" }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 44, flexShrink: 0 }}>
                          <span style={{ fontFamily: "var(--font-heading)", fontWeight: 700, fontSize: 17, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{e.dd}</span>
                          <span style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "#A79E88" }}>{e.day}</span>
                        </div>
                        <span style={{ width: 9, height: 9, borderRadius: "50%", background: e.color, flexShrink: 0, marginTop: 5 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14.5, fontWeight: 600, color: "#33372C", lineHeight: 1.2 }}>{e.titulo || e.typeLabel}</div>
                          <div style={{ fontSize: 12, color: "#9A9280", fontWeight: 500, marginTop: 1 }}>{e.typeLabel}</div>
                        </div>
                        <span style={e.realized
                          ? { flexShrink: 0, fontFamily: "var(--font-heading)", fontWeight: 700, fontSize: 13, fontVariantNumeric: "tabular-nums", color: e.color, background: e.soft, padding: "5px 10px", borderRadius: 8, whiteSpace: "nowrap" }
                          : { flexShrink: 0, fontSize: 11.5, fontWeight: 600, color: "#A79E88", background: "#F0EADB", padding: "5px 10px", borderRadius: 8, whiteSpace: "nowrap" }}>
                          {e.realized ? `${e.valor} pes.` : "cancelado"}
                        </span>
                      </div>
                    );
                  })}
                </AnimatedList>
                {!hasMonthly && <div style={{ padding: "22px 6px", textAlign: "center", fontSize: 13.5, color: "#A79E88" }}>Sem lançamentos neste mês.</div>}
              </div>
            </div>
          </section>
        </div>

        {/* CHART.JS CHARTS */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.65fr", gap: 22, alignItems: "start" }}>
          <section className="card" style={{ borderRadius: 22, padding: "24px 26px 20px" }}>
            <h2 style={{ fontSize: 18, letterSpacing: -0.5, marginBottom: 4 }}>Distribuição por atividade</h2>
            <p style={{ fontSize: 13, color: "#8E8672", fontWeight: 500, marginBottom: 10 }}>{monthLabelLong}</p>
            <div style={{ height: 260 }}>
              {distribuicaoTipos.length > 0 ? (
                <Doughnut
                  data={doughnutData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: "bottom", labels: { font: CHART_FONT, color: "#4A4A3E", padding: 12, usePointStyle: true } },
                      tooltip: { bodyFont: CHART_FONT, titleFont: CHART_FONT },
                    },
                  }}
                />
              ) : (
                <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#A79E88", fontSize: 13.5 }}>
                  Sem dados no período.
                </div>
              )}
            </div>
          </section>

          <section className="card" style={{ borderRadius: 22, padding: "24px 26px 20px" }}>
            <h2 style={{ fontSize: 18, letterSpacing: -0.5, marginBottom: 4 }}>Presenças por mês em {viewYear}</h2>
            <p style={{ fontSize: 13, color: "#8E8672", fontWeight: 500, marginBottom: 10 }}>Todas as atividades · visão anual</p>
            <div style={{ height: 260 }}>
              <Line
                data={lineData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false },
                    tooltip: { bodyFont: CHART_FONT, titleFont: CHART_FONT },
                  },
                  scales: {
                    x: { ticks: { font: CHART_FONT, color: "#8E8672" }, grid: { color: "#EDE6D6" } },
                    y: { beginAtZero: true, ticks: { font: CHART_FONT, color: "#8E8672" }, grid: { color: "#EDE6D6" } },
                  },
                }}
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
